import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import jsPDF from 'jspdf';
import { formatCurrency, generateDetailedPDF } from '../../../utils/reportUtils';
import printerService from '../../../services/printerService';
import { getOrderPaymentBreakdown } from '../../../utils/orderPayments';

const ReporteCaja = ({ shiftId }) => {
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const getFastFoodBaseURL = () => process.env.REACT_APP_FAST_FOOD_SERVICE || 'http://localhost:8002';

    useEffect(() => {
        const fetchReport = async () => {
            try {
                const response = await api.get(`/api/pos/shifts/${shiftId}/report/`, {
                    baseURL: getFastFoodBaseURL()
                });
                
                const shiftData = response.data;
                const normalizedReport = {
                    ...shiftData.summary,
                    shift_info: shiftData.shift_info,
                    orders_detail: shiftData.orders_detail,
                    payment_methods: shiftData.payment_methods,
                    top_products: shiftData.top_products,
                    expenses: shiftData.expenses || [],
                    date: shiftData.shift_info.opened_at,
                    is_shift_report: true,
                    generated_by: shiftData.shift_info.user
                };
                
                setReportData(normalizedReport);
            } catch (err) {
                console.error("Error fetching shift report:", err);
                setError('No se pudo cargar el reporte del turno.');
            } finally {
                setLoading(false);
            }
        };

        if (shiftId) fetchReport();
    }, [shiftId]);

    if (loading) return <div style={{ padding: '3rem', textAlign: 'center', color: '#8a7d70' }}>Cargando reporte...</div>;
    if (error) return <div style={{ padding: '3rem', textAlign: 'center', color: '#b04a3d' }}>{error}</div>;
    if (!reportData) return null;

    const { shift_info } = reportData;
    
    const openingCash = parseFloat(shift_info.opening_cash || 0);
    const closingCash = parseFloat(shift_info.closing_cash || 0); // Este closingCash puede incluir transferencias si fue caja ciega
    const totalSales = parseFloat(reportData.summary?.total_sales || reportData.total_sales || 0);
    const totalExpenses = parseFloat(reportData.summary?.total_expenses || reportData.total_expenses || 0);
    const expensesList = reportData.expenses || [];

    let paymentStats = {
        efectivo: 0,
        efectivo_cop: 0,
        transferencia_cop: 0,
        transferencia: 0,
        tarjeta: 0,
        efectivo_cop_in_usd: 0,
        transferencia_cop_in_usd: 0
    };
    let cancelledStats = { count: 0, total: 0 };
    let employeeStats = { count: 0, total: 0 };
    
    if (reportData.orders_detail && Array.isArray(reportData.orders_detail)) {
        reportData.orders_detail.forEach(order => {
            if (['cancelled'].includes(order.status) || order.status_display?.toLowerCase() === 'anulada') {
                cancelledStats.count += 1;
                cancelledStats.total += parseFloat(order.total_amount || order.total || 0);
            } else if (order.notes && order.notes.includes('[VENTA_EMPLEADO]')) {
                employeeStats.count += 1;
                employeeStats.total += parseFloat(order.total_amount || order.total || 0);
            }
        });
    }

    const exchangeRate = parseFloat(localStorage.getItem('usdExchangeRate')) || 4000;

    if (reportData.orders_detail && Array.isArray(reportData.orders_detail)) {
        reportData.orders_detail.forEach(order => {
            const isCancelled = ['cancelled', 'rejected'].includes(order.status) || order.status_display?.toLowerCase() === 'anulada';
            // Anulada después del cierre: el dinero sí entró en esta caja (el backend envía sus cobros)
            const countedInShift = Array.isArray(order.shift_payments);
            if (isCancelled && !countedInShift) return;

            const isValid = ['delivered', 'completed', 'preparing', 'confirmed', 'ready'].includes(order.status) ||
                            ['paid', 'partially_paid'].includes(order.payment_status);
            if (!isValid && !countedInShift) return;

            const breakdown = getOrderPaymentBreakdown(order, exchangeRate);
            Object.keys(breakdown).forEach(key => { paymentStats[key] += breakdown[key]; });
        });
    }

    // EXTRAER TRANSFERENCIAS Y EFECTIVO FÍSICO DE LAS NOTAS (Caja Ciega)
    let transferenciasFisico = 0;
    let efectivoFisicoDeclarado = closingCash;
    let transferenciasFisicoCOP = 0;
    let efectivoFisicoDeclaradoCOP = 0;
    
    if (shift_info.closing_notes) {
        // Formato antiguo
        if (shift_info.closing_notes.includes('Transferencias: $')) {
            const matchT = shift_info.closing_notes.match(/Transferencias:\s*\$?([\d.]+)/);
            if (matchT) {
                transferenciasFisico = parseFloat(matchT[1]);
            }
            efectivoFisicoDeclarado = closingCash - transferenciasFisico;
        }
        // Formato nuevo
        if (shift_info.closing_notes.includes('FisicoTransferUSD=')) {
            const matchT = shift_info.closing_notes.match(/FisicoTransferUSD=([\d.]+)/);
            if (matchT) {
                transferenciasFisico = parseFloat(matchT[1]) || 0;
            }
            const matchE = shift_info.closing_notes.match(/FisicoEfectivoUSD=([\d.]+)/);
            if (matchE) {
                efectivoFisicoDeclarado = parseFloat(matchE[1]) || 0;
            } else {
                efectivoFisicoDeclarado = closingCash - transferenciasFisico;
            }
            if (efectivoFisicoDeclarado === 0 && closingCash > 0) {
                efectivoFisicoDeclarado = closingCash;
            }
            // COP
            const matchTCop = shift_info.closing_notes.match(/FisicoTransferCOP=([\d.]+)/);
            if (matchTCop) transferenciasFisicoCOP = parseFloat(matchTCop[1]) || 0;
            const matchECop = shift_info.closing_notes.match(/FisicoEfectivoCOP=([\d.]+)/);
            if (matchECop) {
                efectivoFisicoDeclaradoCOP = parseFloat(matchECop[1]) || 0;
            }
            if (!efectivoFisicoDeclaradoCOP && shift_info.closing_cash_cop) {
                efectivoFisicoDeclaradoCOP = parseFloat(shift_info.closing_cash_cop || 0);
            }
        }
    }

    // Gastos separados
    const expensesListUSD = expensesList.filter(e => e.description && e.description.startsWith('[USD]'));
    const expensesListCOP = expensesList.filter(e => !e.description || !e.description.startsWith('[USD]'));
    const totalExpensesUSD = expensesListUSD.reduce((sum, e) => sum + parseFloat(e.amount), 0);
    const totalExpensesCOP = expensesListCOP.reduce((sum, e) => sum + (e.amount_cop !== null && e.amount_cop !== undefined ? parseFloat(e.amount_cop) : parseFloat(e.amount) * exchangeRate), 0);

    const efectivoTotalBruto = openingCash + paymentStats.efectivo;
    const efectivoEsperadoFinal = efectivoTotalBruto - totalExpensesUSD; 
    
    const sobranteEfectivo = efectivoFisicoDeclarado - efectivoEsperadoFinal;
    
    const transferenciasSistema = paymentStats.transferencia;
    const sobranteTransferencia = transferenciasFisico - transferenciasSistema;

    // COP Totals
    const efectivoTotalBrutoCOP = paymentStats.efectivo_cop;
    const efectivoEsperadoFinalCOP = efectivoTotalBrutoCOP - totalExpensesCOP;
    const sobranteEfectivoCOP = efectivoFisicoDeclaradoCOP - efectivoEsperadoFinalCOP;
    
    const transferenciasSistemaCOP = paymentStats.transferencia_cop;
    const sobranteTransferenciaCOP = transferenciasFisicoCOP - transferenciasSistemaCOP;

    const handlePrintCaja = async () => {
        const countSales = parseInt(reportData.total_orders || 0);
        const totalSales = parseFloat(reportData.total_sales || 0);

        const chars_per_line = 48;
        let lines = [];
        
        const center = (text) => {
            const padding = Math.max(0, Math.floor((chars_per_line - text.length) / 2));
            return ' '.repeat(padding) + text;
        };

        const rightAlign = (label, value) => {
            const valueStr = String(value);
            const padding = Math.max(0, chars_per_line - label.length - valueStr.length);
            return label + ' '.repeat(padding) + valueStr;
        };

        const formatRow3 = (label, count, amount) => {
            const countStr = `: ${count}`;
            const amountStr = `(${parseFloat(amount).toFixed(2)})`;
            let labelPad = label.length > 16 ? label.substring(0, 16) : label.padEnd(16, ' ');
            let countPad = countStr.length > 6 ? countStr.substring(0, 6) : countStr.padEnd(6, ' ');
            let amountPad = amountStr.padStart(10, ' ');
            return labelPad + countPad + amountPad;
        };

        const current_time = new Date();
        const pad0 = (n) => n.toString().padStart(2, '0');
        const dateStr = `${current_time.getFullYear()}-${pad0(current_time.getMonth()+1)}-${pad0(current_time.getDate())} ${pad0(current_time.getHours())}:${pad0(current_time.getMinutes())}:${pad0(current_time.getSeconds())}`;
        
        lines.push(center("CIERRE DE CAJA"));
        lines.push(center(`#${shift_info.number}`));
        lines.push(center(dateStr));
        lines.push(center(`USUARIO: ${shift_info.user || shift_info.manager_name}`));
        lines.push("");

        if (expensesList.length > 0) {
            lines.push(center("GASTOS DE CAJA"));
            expensesList.forEach(exp => {
                const desc = "- " + exp.description;
                const val = `-${parseFloat(exp.amount).toFixed(2)}`;
                const maxDescLen = chars_per_line - val.length - 1;
                const truncatedDesc = desc.length > maxDescLen ? desc.substring(0, maxDescLen) : desc;
                lines.push(rightAlign(truncatedDesc, val));
            });
            lines.push("-".repeat(chars_per_line));
        }

        lines.push(center("RESUMEN DEL CIERRE"));
        lines.push("-".repeat(chars_per_line));
        
        lines.push(formatRow3("TOTAL VENTAS", countSales, totalSales));
        lines.push(formatRow3("VENTAS ANULADAS", cancelledStats.count, cancelledStats.total));
        lines.push("-".repeat(chars_per_line));
        
        lines.push(rightAlign("EFECTIVO", paymentStats.efectivo.toFixed(2)));
        lines.push(rightAlign("TRANSFERENCIA", paymentStats.transferencia.toFixed(2)));
        if (paymentStats.tarjeta > 0) {
            lines.push(rightAlign("TARJETA", paymentStats.tarjeta.toFixed(2)));
        }
        lines.push("-".repeat(chars_per_line));
        
        lines.push(formatRow3("TOTAL GASTOS", expensesList.length, `-${totalExpenses.toFixed(2)}`));
        lines.push(formatRow3("TOTAL INGRESOS", 0, "0.00"));
        lines.push(formatRow3("OTROS/EXTRAS", 0, "0.00"));
        lines.push("");

        lines.push(center("CAJA"));
        lines.push("-".repeat(chars_per_line));
        
        lines.push("[EFECTIVO]");
        lines.push(rightAlign("SISTEMA", efectivoTotalBruto.toFixed(2)));
        lines.push(rightAlign("- GASTOS", totalExpensesUSD.toFixed(2)));
        lines.push(rightAlign("ESPERADO", efectivoEsperadoFinal.toFixed(2)));
        lines.push(rightAlign("CONTEO FISICO", efectivoFisicoDeclarado.toFixed(2)));
        lines.push(rightAlign("DIFERENCIA", sobranteEfectivo.toFixed(2)));
        lines.push("");

        lines.push("[TRANSFERENCIA]");
        lines.push(rightAlign("SISTEMA", transferenciasSistema.toFixed(2)));
        lines.push(rightAlign("CONTEO FISICO", transferenciasFisico.toFixed(2)));
        lines.push(rightAlign("DIFERENCIA", sobranteTransferencia.toFixed(2)));
        lines.push("");

        const totalSistema = efectivoEsperadoFinal + transferenciasSistema;
        const totalConteo = efectivoFisicoDeclarado + transferenciasFisico;
        const totalDiferencia = sobranteEfectivo + sobranteTransferencia;

        lines.push("RESUMEN:");
        lines.push(rightAlign("SISTEMA", totalSistema.toFixed(2)));
        lines.push(rightAlign("CONTEO FISICO", totalConteo.toFixed(2)));
        lines.push(rightAlign("DIFERENCIA", totalDiferencia.toFixed(2)));
        lines.push("");

        let horizCOP_ticket = [];
        if (shift_info.closing_notes) {
            let notesText = shift_info.closing_notes;
            if (notesText.includes('[CIERRE_CIEGO_V2]')) {
                const parts = notesText.split('---\n');
                notesText = parts.length > 1 ? parts[1] : '';
            } else if (notesText.includes('Cierre Ciego. Desglose:\n')) {
                notesText = notesText.replace('Cierre Ciego. Desglose:\n', '');
            }
            
            notesText = notesText.trim();
            if (notesText) {
                const detLines = notesText.split('\n');
                let horiz = [];
                let inCOPSection = false;
                detLines.forEach(l => {
                    if (l.includes('PESOS (COP):') || l.includes('Caja COP:')) {
                        inCOPSection = true;
                        return;
                    }
                    const match = l.match(/([^:]+):\s*(\d+)/);
                    if (match && !l.includes('USD') && !l.includes('COP') && !l.includes('Fisico')) {
                        let name = match[1].trim().replace('Billetes de ', 'B.');
                        if (inCOPSection) {
                            horizCOP_ticket.push(`${name}=${match[2]}`);
                        } else {
                            horiz.push(`${name}=${match[2]}`);
                        }
                    }
                });

                if (horiz.length > 0) {
                    lines.push("DETALLE DE MONEDAS:");
                    let outLine = "";
                    horiz.forEach(item => {
                        if ((outLine + item).length > chars_per_line) {
                            lines.push(outLine.trim());
                            outLine = "";
                        }
                        outLine += item + "  ";
                    });
                    if (outLine) lines.push(outLine.trim());
                }
            }
        }
        
        lines.push("");
        lines.push(center("CAJA (COP)"));
        lines.push("-".repeat(chars_per_line));
        
        if (horizCOP_ticket.length > 0) {
            lines.push("DETALLE DE MONEDAS:");
            let outLine = "";
            horizCOP_ticket.forEach(item => {
                if ((outLine + item).length > chars_per_line) {
                    lines.push(outLine.trim());
                    outLine = "";
                }
                outLine += item + "  ";
            });
            if (outLine) lines.push(outLine.trim());
            lines.push("-".repeat(chars_per_line));
        }

        lines.push("[EFECTIVO]");
        lines.push(rightAlign("SISTEMA", efectivoTotalBrutoCOP.toLocaleString('es-CO')));
        lines.push(rightAlign("- GASTOS", totalExpensesCOP.toLocaleString('es-CO')));
        lines.push(rightAlign("ESPERADO", efectivoEsperadoFinalCOP.toLocaleString('es-CO')));
        lines.push(rightAlign("CONTEO FISICO", efectivoFisicoDeclaradoCOP.toLocaleString('es-CO')));
        lines.push(rightAlign("DIFERENCIA", sobranteEfectivoCOP.toLocaleString('es-CO')));
        lines.push("");

        lines.push("[TRANSFERENCIA]");
        lines.push(rightAlign("SISTEMA", transferenciasSistemaCOP.toLocaleString('es-CO')));
        lines.push(rightAlign("CONTEO FISICO", transferenciasFisicoCOP.toLocaleString('es-CO')));
        lines.push(rightAlign("DIFERENCIA", sobranteTransferenciaCOP.toLocaleString('es-CO')));
        lines.push("");

        const totalSistemaCOP = efectivoEsperadoFinalCOP + transferenciasSistemaCOP;
        const totalConteoCOP = efectivoFisicoDeclaradoCOP + transferenciasFisicoCOP;
        const totalDiferenciaCOP = sobranteEfectivoCOP + sobranteTransferenciaCOP;

        lines.push("RESUMEN:");
        lines.push(rightAlign("SISTEMA", totalSistemaCOP.toLocaleString('es-CO')));
        lines.push(rightAlign("CONTEO FISICO", totalConteoCOP.toLocaleString('es-CO')));
        lines.push(rightAlign("DIFERENCIA", totalDiferenciaCOP.toLocaleString('es-CO')));
        lines.push("");
        lines.push("-".repeat(chars_per_line));
        
        const ticketContent = lines.join("\n");

        try {
            await printerService.printCustomTicket(ticketContent, 'report');
        } catch (error) {
            alert('Error al enviar impresión. Revisa la consola para más detalles.');
        }
    };

    const handleDownloadPDFCaja = () => {
        const countSales = parseInt(reportData.total_orders || 0);
        const totalSales = parseFloat(reportData.total_sales || 0);

        const chars_per_line = 54;
        let lines = [];
        
        const center = (text) => ' '.repeat(Math.max(0, Math.floor((chars_per_line - text.length) / 2))) + text;
        const rightAlign = (label, value) => {
            const valueStr = String(value);
            const padding = Math.max(0, chars_per_line - label.length - valueStr.length);
            return label + ' '.repeat(padding) + valueStr;
        };
        const formatRow3 = (label, count, amount) => {
            const countStr = `: ${count}`;
            const amountStr = `(${parseFloat(amount).toFixed(2)})`;
            const labelPad = label.padEnd(22, ' ');
            const countPad = countStr.padEnd(12, ' ');
            const amountPad = amountStr.padStart(20, ' ');
            return labelPad + countPad + amountPad;
        };

        const current_time = new Date();
        const pad0 = (n) => n.toString().padStart(2, '0');
        const dateStr = `${current_time.getFullYear()}-${pad0(current_time.getMonth()+1)}-${pad0(current_time.getDate())} ${pad0(current_time.getHours())}:${pad0(current_time.getMinutes())}:${pad0(current_time.getSeconds())}`;
        
        lines.push(center(`CIERRE DE CAJA #${shift_info.number}`));
        lines.push(center(dateStr));
        lines.push(center(`USUARIO: ${shift_info.user || shift_info.manager_name}`));
        lines.push("");

        if (expensesList.length > 0) {
            lines.push(center("GASTOS DE CAJA"));
            expensesList.forEach(exp => {
                const desc = "- " + exp.description;
                lines.push(rightAlign(desc.length > 30 ? desc.substring(0, 30) : desc, `-${parseFloat(exp.amount).toFixed(2)}`));
            });
            lines.push("-".repeat(chars_per_line));
        }

        lines.push(center("RESUMEN DEL CIERRE"));
        lines.push("-".repeat(chars_per_line));
        
        lines.push(formatRow3("TOTAL VENTAS", countSales, totalSales));
        lines.push(formatRow3("VENTAS ANULADAS", cancelledStats.count, cancelledStats.total));
        lines.push("-".repeat(chars_per_line));
        
        lines.push(rightAlign("EFECTIVO", paymentStats.efectivo.toFixed(2)));
        lines.push(rightAlign("TRANSFERENCIA", paymentStats.transferencia.toFixed(2)));
        if (paymentStats.tarjeta > 0) {
            lines.push(rightAlign("TARJETA", paymentStats.tarjeta.toFixed(2)));
        }
        lines.push("-".repeat(chars_per_line));
        
        lines.push(formatRow3("TOTAL GASTOS", expensesList.length, `-${totalExpenses.toFixed(2)}`));
        lines.push(formatRow3("TOTAL INGRESOS", 0, "0.00"));
        lines.push(formatRow3("TOTAL OTROS/EXTRAS", 0, "0.00"));
        lines.push("");

        lines.push(center("CAJA"));
        lines.push("-".repeat(chars_per_line));
        
        lines.push("[EFECTIVO]");
        lines.push(rightAlign("SISTEMA", efectivoTotalBruto.toFixed(2)));
        lines.push(rightAlign("- GASTOS", totalExpensesUSD.toFixed(2)));
        lines.push(rightAlign("ESPERADO", efectivoEsperadoFinal.toFixed(2)));
        lines.push(rightAlign("CONTEO FISICO", efectivoFisicoDeclarado.toFixed(2)));
        lines.push(rightAlign("DIFERENCIA", sobranteEfectivo.toFixed(2)));
        lines.push("");

        lines.push("[TRANSFERENCIA]");
        lines.push(rightAlign("SISTEMA", transferenciasSistema.toFixed(2)));
        lines.push(rightAlign("CONTEO FISICO", transferenciasFisico.toFixed(2)));
        lines.push(rightAlign("DIFERENCIA", sobranteTransferencia.toFixed(2)));
        lines.push("");

        const totalSistema = efectivoEsperadoFinal + transferenciasSistema;
        const totalConteo = efectivoFisicoDeclarado + transferenciasFisico;
        const totalDiferencia = sobranteEfectivo + sobranteTransferencia;

        lines.push("RESUMEN:");
        lines.push(rightAlign("SISTEMA", totalSistema.toFixed(2)));
        lines.push(rightAlign("CONTEO FISICO", totalConteo.toFixed(2)));
        lines.push(rightAlign("DIFERENCIA", totalDiferencia.toFixed(2)));
        lines.push("");

        let horizCOP_pdf = [];
        if (shift_info.closing_notes) {
            let notesText = shift_info.closing_notes;
            if (notesText.includes('[CIERRE_CIEGO_V2]')) {
                const parts = notesText.split('---\n');
                notesText = parts.length > 1 ? parts[1] : '';
            } else if (notesText.includes('Cierre Ciego. Desglose:\n')) {
                notesText = notesText.replace('Cierre Ciego. Desglose:\n', '');
            }
            
            notesText = notesText.trim();
            if (notesText) {
                const detLines = notesText.split('\n');
                let horiz = [];
                let inCOPSection = false;
                detLines.forEach(l => {
                    if (l.includes('PESOS (COP):') || l.includes('Caja COP:')) {
                        inCOPSection = true;
                        return;
                    }
                    const match = l.match(/([^:]+):\s*(\d+)/);
                    if (match && !l.includes('USD') && !l.includes('COP') && !l.includes('Fisico')) {
                        let name = match[1].trim().replace('Billetes de ', 'B.');
                        if (inCOPSection) {
                            horizCOP_pdf.push(`${name}=${match[2]}`);
                        } else {
                            horiz.push(`${name}=${match[2]}`);
                        }
                    }
                });

                if (horiz.length > 0) {
                    lines.push("DETALLE DE MONEDAS:");
                    let outLine = "";
                    horiz.forEach(item => {
                        if ((outLine + item).length > chars_per_line) {
                            lines.push(outLine.trim());
                            outLine = "";
                        }
                        outLine += item + "  ";
                    });
                    if (outLine) lines.push(outLine.trim());
                }
            }
        }
        
        lines.push("");
        lines.push(center("CAJA (COP)"));
        lines.push("-".repeat(chars_per_line));
        
        if (horizCOP_pdf.length > 0) {
            lines.push("DETALLE DE MONEDAS:");
            let outLine = "";
            horizCOP_pdf.forEach(item => {
                if ((outLine + item).length > chars_per_line) {
                    lines.push(outLine.trim());
                    outLine = "";
                }
                outLine += item + "  ";
            });
            if (outLine) lines.push(outLine.trim());
            lines.push("-".repeat(chars_per_line));
        }

        lines.push("[EFECTIVO]");
        lines.push(rightAlign("SISTEMA", efectivoTotalBrutoCOP.toLocaleString('es-CO')));
        lines.push(rightAlign("- GASTOS", totalExpensesCOP.toLocaleString('es-CO')));
        lines.push(rightAlign("ESPERADO", efectivoEsperadoFinalCOP.toLocaleString('es-CO')));
        lines.push(rightAlign("CONTEO FISICO", efectivoFisicoDeclaradoCOP.toLocaleString('es-CO')));
        lines.push(rightAlign("DIFERENCIA", sobranteEfectivoCOP.toLocaleString('es-CO')));
        lines.push("");

        lines.push("[TRANSFERENCIA]");
        lines.push(rightAlign("SISTEMA", transferenciasSistemaCOP.toLocaleString('es-CO')));
        lines.push(rightAlign("CONTEO FISICO", transferenciasFisicoCOP.toLocaleString('es-CO')));
        lines.push(rightAlign("DIFERENCIA", sobranteTransferenciaCOP.toLocaleString('es-CO')));
        lines.push("");

        const totalSistemaCOP2 = efectivoEsperadoFinalCOP + transferenciasSistemaCOP;
        const totalConteoCOP2 = efectivoFisicoDeclaradoCOP + transferenciasFisicoCOP;
        const totalDiferenciaCOP2 = sobranteEfectivoCOP + sobranteTransferenciaCOP;

        lines.push("RESUMEN:");
        lines.push(rightAlign("SISTEMA", totalSistemaCOP2.toLocaleString('es-CO')));
        lines.push(rightAlign("CONTEO FISICO", totalConteoCOP2.toLocaleString('es-CO')));
        lines.push(rightAlign("DIFERENCIA", totalDiferenciaCOP2.toLocaleString('es-CO')));
        lines.push("");
        lines.push("-".repeat(chars_per_line));

        const lineSpacing = 3.5;
        const docHeight = Math.max(60, (lines.length * lineSpacing) + 15);
        const doc = new jsPDF({ format: [80, docHeight] });
        doc.setFont('courier', 'normal');
        doc.setFontSize(6.6);
        
        let y = 10;
        lines.forEach(line => {
            doc.text(line, 2, y);
            y += lineSpacing;
        });

        doc.save(`Cierre_Caja_${shift_info.number}.pdf`);
    };

    const handlePrintVentas = () => {
        generateDetailedPDF(reportData, 'Reporte de Ventas', `Turno #${shift_info.number}`);
    };

    const styles = {
        container: { fontFamily: 'inherit' },
        headerActions: { display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #efe8dc' },
        titleBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' },
        buttonGroup: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
        titleInfo: { fontSize: '1.1rem', fontWeight: '600', color: '#1a1310', marginBottom: '0.75rem' },
        textRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', color: '#6b5e51' },
        textTotal: { display: 'flex', justifyContent: 'space-between', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px dashed #d6ccbc', fontWeight: '700', color: '#1a1310' },
        section: { backgroundColor: '#faf7f1', padding: '1.25rem', borderRadius: '12px', marginBottom: '1.5rem' },
        btnPDFCaja: { 
            backgroundColor: 'var(--primary-color)', color: '#fff', border: 'none', padding: '0.6rem 1rem', 
            borderRadius: '6px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '1'
        },
        btnDownloadPDFCaja: { 
            backgroundColor: '#4a3222', color: '#fff', border: 'none', padding: '0.6rem 1rem', 
            borderRadius: '6px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '1'
        },
        btnPDFVentas: { 
            backgroundColor: 'var(--secondary-color)', color: 'var(--primary-color)', border: 'none', padding: '0.6rem 1rem', 
            borderRadius: '6px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '1'
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.headerActions}>
                <div style={styles.titleBar}>
                    <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#1a1310' }}>Turno: {shift_info.number}</h2>
                </div>
                <div style={styles.buttonGroup}>
                    <button onClick={handleDownloadPDFCaja} style={styles.btnDownloadPDFCaja}>
                        <i className="bi bi-file-pdf"></i> PDF de Caja
                    </button>
                    <button onClick={handlePrintVentas} style={styles.btnPDFVentas}>
                        <i className="bi bi-file-pdf"></i> PDF de Ventas
                    </button>
                    <button onClick={handlePrintCaja} style={styles.btnPDFCaja}>
                        <i className="bi bi-printer"></i> Imprimir Ticket
                    </button>
                </div>
            </div>

            <div style={styles.section}>
                <h3 style={styles.titleInfo}>Información General</h3>
                <div style={styles.textRow}><span>Encargado:</span> <strong>{shift_info.user || shift_info.manager_name}</strong></div>
                <div style={styles.textRow}><span>Apertura:</span> <span>{new Date(shift_info.opened_at).toLocaleString()}</span></div>
                <div style={styles.textRow}><span>Cierre:</span> <span>{shift_info.closed_at ? new Date(shift_info.closed_at).toLocaleString() : 'En curso'}</span></div>
            </div>

            {/* SECCIÓN DE EFECTIVO */}
            <div style={{ backgroundColor: '#f0e6d6', padding: '1.25rem', borderRadius: '12px', marginBottom: '1.5rem' }}>
                <h3 style={{ ...styles.titleInfo, color: '#3a2618' }}>Efectivo</h3>
                <div style={styles.textRow}>
                    <span>Efectivo Inicial:</span> 
                    <span>${openingCash.toFixed(2)}</span>
                </div>
                <div style={styles.textRow}>
                    <span>Ventas del Turno:</span> 
                    <span>${paymentStats.efectivo.toFixed(2)}</span>
                </div>
                {totalExpenses > 0 && (
                    <div style={styles.textRow}>
                        <span>Gastos en USD:</span> 
                        <span style={{ color: 'var(--danger-color)' }}>-${totalExpensesUSD.toFixed(2)}</span>
                    </div>
                )}
                <div style={{ ...styles.textTotal, borderColor: '#3a2618', color: '#3a2618', fontSize: '1.1rem' }}>
                    <span>Total Efectivo Esperado:</span> 
                    <span>${efectivoEsperadoFinal.toFixed(2)}</span>
                </div>
                
                <div style={{ ...styles.textRow, marginTop: '1rem' }}>
                    <span>Efec. Físico (Cajero):</span> 
                    <span>${efectivoFisicoDeclarado.toFixed(2)}</span>
                </div>
                {shift_info.closing_cash_cop != null && (
                    <div style={{ ...styles.textRow }}>
                        <span>Efec. Físico COP:</span> 
                        <span style={{ color: '#3a2618' }}>${parseFloat(shift_info.closing_cash_cop).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    </div>
                )}
                <div style={{ ...styles.textTotal, border: 'none', color: sobranteEfectivo >= 0 ? 'var(--success-color)' : 'var(--danger-color)', fontSize: '1.1rem', marginTop: '0.5rem', paddingTop: '0.5rem' }}>
                    <span>SOBRANTE / FALTANTE EFEC.:</span> 
                    <span>{sobranteEfectivo >= 0 ? '+' : ''}${sobranteEfectivo.toFixed(2)}</span>
                </div>
            </div>

            {/* SECCIÓN DE TRANSFERENCIAS */}
            <div style={{ backgroundColor: '#eef4e8', padding: '1.25rem', borderRadius: '12px', marginBottom: '1.5rem' }}>
                <h3 style={{ ...styles.titleInfo, color: '#3d5c37' }}>Transferencias</h3>
                <div style={{ ...styles.textRow, fontWeight: 'bold' }}>
                    <span>Transferencias en Sistema:</span> 
                    <span>${transferenciasSistema.toFixed(2)}</span>
                </div>
                <div style={{ ...styles.textRow, fontWeight: 'bold' }}>
                    <span>Transferencias en Físico (Contado):</span> 
                    <span>${transferenciasFisico.toFixed(2)}</span>
                </div>
                <div style={{ ...styles.textRow, fontWeight: 'bold', color: sobranteTransferencia >= 0 ? '#3d5c37' : 'var(--danger-color)' }}>
                    <span>Sobrante / Faltante Transferencias:</span> 
                    <span>{sobranteTransferencia >= 0 ? '+' : ''}${sobranteTransferencia.toFixed(2)}</span>
                </div>
            </div>

            <div style={styles.section}>
                <h3 style={styles.titleInfo}>Total General de Ventas (Todos los métodos)</h3>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--primary-color)', textAlign: 'center' }}>
                    ${totalSales.toFixed(2)}
                </div>
            </div>

            {/* SECCIÓN DE VENTAS ANULADAS */}
            <div style={{ backgroundColor: '#f7e5e0', padding: '1.25rem', borderRadius: '12px', marginBottom: '1.5rem' }}>
                <h3 style={{ ...styles.titleInfo, color: '#7a3a2a' }}>Ventas Anuladas</h3>
                <div style={{ ...styles.textRow, fontWeight: 'bold' }}>
                    <span>Cantidad de Ventas Anuladas:</span> 
                    <span>{cancelledStats.count}</span>
                </div>
                <div style={{ ...styles.textRow, fontWeight: 'bold', color: '#7a3a2a' }}>
                    <span>Total Ventas Anuladas:</span> 
                    <span>${cancelledStats.total.toFixed(2)}</span>
                </div>
            </div>

            {/* SECCIÓN DE VENTAS A EMPLEADOS */}
            <div style={{ backgroundColor: '#f0e6d6', padding: '1.25rem', borderRadius: '12px', marginBottom: '1.5rem' }}>
                <h3 style={{ ...styles.titleInfo, color: '#2b211b' }}>Ventas a Empleados</h3>
                <div style={{ ...styles.textRow, fontWeight: 'bold' }}>
                    <span>Cantidad de Ventas:</span> 
                    <span>{employeeStats.count}</span>
                </div>
                <div style={{ ...styles.textRow, fontWeight: 'bold', color: '#2b211b' }}>
                    <span>Total Ventas:</span> 
                    <span>${employeeStats.total.toFixed(2)}</span>
                </div>
            </div>

            {/* GASTOS */}
            {expensesList.length > 0 && (
                <div style={styles.section}>
                    <h3 style={styles.titleInfo}>Detalle de Gastos</h3>
                    {expensesList.map(exp => (
                        <div key={exp.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e6dfd3' }}>
                            <span style={{ color: '#6b5e51' }}>{exp.description} <br/><small style={{color: '#8a7d70'}}>{new Date(exp.created_at).toLocaleTimeString()}</small></span>
                            <span style={{ fontWeight: '600', color: 'var(--danger-color)' }}>${parseFloat(exp.amount).toFixed(2)}</span>
                        </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', fontWeight: 'bold' }}>
                        <span>TOTAL GASTOS</span>
                        <span style={{ color: 'var(--danger-color)' }}>${totalExpenses.toFixed(2)}</span>
                    </div>
                </div>
            )}
            
            {/* NOTAS */}
            {(shift_info.opening_notes || shift_info.closing_notes) && (
                <div style={{ ...styles.section, backgroundColor: '#f7ecd3' }}>
                    <h3 style={{ ...styles.titleInfo, color: '#6f4d17' }}>Notas</h3>
                    {shift_info.opening_notes && <div style={{ marginBottom: '0.5rem', color: '#6f4d17' }}><strong>Apertura:</strong> {shift_info.opening_notes}</div>}
                    {shift_info.closing_notes && <div style={{ color: '#6f4d17', whiteSpace: 'pre-wrap' }}><strong>Cierre:</strong><br/>{shift_info.closing_notes}</div>}
                </div>
            )}
        </div>
    );
};

export default ReporteCaja;
